/**
 * Person names as the product shows them. Given and family names are stored apart
 * (patients: `first_name` / `last_name`; staff: Better Auth additional fields) and every
 * display string is *formatted* here — nothing parses a display string back into parts.
 * Decision record: docs/PLAN-person-names.md.
 */
export interface PersonNameParts {
  first: string;
  last: string | null;
}

/** "Імʼя Прізвище" — the one place the display order is written; "Імʼя" when no surname. */
export function formatPersonName(p: PersonNameParts): string {
  return p.last ? `${p.first} ${p.last}` : p.first;
}

/** "Прізвище І." for documents; the given name alone when there is no surname. */
export function formalPersonName(p: PersonNameParts): string {
  if (!p.last) return p.first;
  const initial = p.first[0];
  return initial ? `${p.last} ${initial.toUpperCase()}.` : p.last;
}

/** Up to two initials from the parts ("Андрій" + "Ко." → "АК"); "?" when blank. */
export function initialsOf(p: PersonNameParts): string {
  const first = p.first.trim()[0] ?? "?";
  const last = p.last?.trim()[0] ?? "";
  return `${first}${last}`.toUpperCase();
}

const APOSTROPHE_VARIANTS = /['\u2019\u2018\u02B9`\u00B4]/g;
/** ʼ U+02BC — the Ukrainian apostrophe ("Лукʼян"); keyboards produce ' ’ ‘ ʹ ` ´ too. */
export const APOSTROPHE = "\u02BC";

/** Every apostrophe variant → ʼ, so a typed "Лук'ян" and a stored "Лукʼян" compare equal. */
export function foldApostrophes(s: string): string {
  return s.replace(APOSTROPHE_VARIANTS, APOSTROPHE);
}

/**
 * Client-side counterpart of the API's token search: every whitespace token of `query` must
 * occur in at least one haystack, case-insensitively, any order, any apostrophe —
 * "Ко. Андрій" finds "Андрій Ко.". A blank query matches everything.
 */
export function matchesQuery(haystacks: (string | null | undefined)[], query: string): boolean {
  const tokens = foldApostrophes(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const fields = haystacks.map((h) => foldApostrophes(h ?? "").toLowerCase());
  return tokens.every((token) => fields.some((field) => field.includes(token)));
}

/**
 * Initials of a display string ("Андрій Ко." → "АК", "Лукʼян" → "Л"; "?" when blank) — for
 * Better Auth's `user.name` and the `patientName` strings read models deliver. A formatter
 * over the first two whitespace tokens, never a parser of which token is which.
 */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? "";
  return `${first}${second}`.toUpperCase();
}
